import React, { useEffect, useState } from 'react';
import { Prompt } from '../types/admin';
import { Agent } from '../types/agent';

interface PromptsListProps {
  prompts: Prompt[];
  onSelect: (id: number) => void;
  onDuplicate: (id: number, newName?: string) => void;
  onDelete: (id: number) => void;
  loading: boolean;
}

const PromptsList: React.FC<PromptsListProps> = ({
  prompts,
  onSelect,
  onDuplicate,
  onDelete,
  loading
}) => {
  const [agentPromptIds, setAgentPromptIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7000';
        const response = await fetch(`${apiBaseUrl}/api/admin/agents`);
        if (response.ok) {
          const text = await response.text();
          if (text.startsWith('{') || text.startsWith('[')) {
            const data = JSON.parse(text);
            const promptIds = new Set<number>();
            data.agents?.forEach((agent: Agent) => {
              if (agent.systemPromptId) promptIds.add(agent.systemPromptId);
              if (agent.conversationPromptId) promptIds.add(agent.conversationPromptId);
            });
            setAgentPromptIds(promptIds);
          }
        }
      } catch (error) {
        console.error('Error fetching agents:', error);
      }
    };
    fetchAgents();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPromptTypeBadge = (prompt: Prompt) => {
    if (prompt.id === 1) return <span className="badge system">System</span>;
    
    if (agentPromptIds.has(prompt.id)) {
      return <span className="badge agent">Prompt Agent</span>;
    }
    
    return <span className="badge prompt">Prompt</span>;
  };

  const handleDuplicate = (prompt: Prompt) => {
    const newName = window.prompt('Nombre para el prompt duplicado:', `${prompt.name} (Copia)`);
    if (newName) {
      onDuplicate(prompt.id, newName);
    }
  };

  if (loading) {
    return <div className="loading">Cargando prompts...</div>;
  }

  return (
    <div className="prompts-list">
      <div className="list-header">
        <h3>Prompts Disponibles ({prompts.length})</h3>
      </div>

      <div className="prompts-scroll-container">
        <div className="prompts-grid">
          {prompts.map((prompt) => (
            <div key={prompt.id} className="prompt-card">
              <div className="prompt-header">
                <div className="prompt-title">
                  <span className="prompt-id">#{prompt.id}</span>
                  <span className="prompt-name">{prompt.name}</span>
                  {getPromptTypeBadge(prompt)}
                </div>
                <div className="prompt-actions">
                  <button
                    className="action-btn edit"
                    onClick={() => onSelect(prompt.id)}
                    title="Editar"
                  >
                    ✏️
                  </button>
                  <button
                    className="action-btn duplicate"
                    onClick={() => handleDuplicate(prompt)}
                    title="Duplicar"
                  >
                    📋
                  </button>
                  {prompt.id !== 1 && (
                    <button
                      className="action-btn delete"
                      onClick={() => onDelete(prompt.id)}
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>

              <div className="prompt-content">
                <div className="prompt-text">
                  {prompt.prompt_text.substring(0, 150)}
                  {prompt.prompt_text.length > 150 && '...'}
                </div>
              </div>

              <div className="prompt-meta">
                <div className="meta-item">
                  <span className="label">Versión:</span>
                  <span className="value">{prompt.version || 1}</span>
                </div>
                <div className="meta-item">
                  <span className="label">Temp:</span>
                  <span className="value">{prompt.temperature}</span>
                </div>
                <div className="meta-item">
                  <span className="label">Tokens:</span>
                  <span className="value">{prompt.max_tokens}</span>
                </div>
                <div className="meta-item">
                  <span className="label">Actualizado:</span>
                  <span className="value">{formatDate(prompt.updated_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .prompts-list {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .list-header {
          flex-shrink: 0;
        }

        .list-header h3 {
          margin: 0 0 20px 0;
          color: #333;
          font-size: 18px;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
          font-size: 16px;
        }

        .prompts-scroll-container {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          min-height: 0;
          padding-right: 10px;
        }

        .prompts-scroll-container::-webkit-scrollbar {
          width: 8px;
        }

        .prompts-scroll-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .prompts-scroll-container::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        .prompts-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        .prompts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
          padding-bottom: 20px;
        }

        .prompt-card {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 16px;
          background: white;
          transition: all 0.2s;
        }

        .prompt-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          border-color: #4CAF50;
        }

        .prompt-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .prompt-title {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
        }

        .prompt-id {
          background: #f5f5f5;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 12px;
          color: #666;
          font-weight: bold;
        }

        .prompt-name {
          font-weight: bold;
          color: #333;
          font-size: 14px;
        }

        .badge {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: bold;
          text-transform: uppercase;
        }

        .badge.system {
          background: #ff5722;
          color: white;
        }

        .badge.agent {
          background: #4CAF50;
          color: white;
        }

        .badge.prompt {
          background: #2196F3;
          color: white;
        }

        .prompt-actions {
          display: flex;
          gap: 4px;
        }

        .action-btn {
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          border-radius: 4px;
          font-size: 14px;
          transition: background 0.2s;
        }

        .action-btn:hover {
          background: #f5f5f5;
        }

        .prompt-content {
          margin-bottom: 12px;
        }

        .prompt-text {
          background: #f9f9f9;
          padding: 12px;
          border-radius: 4px;
          font-size: 13px;
          line-height: 1.4;
          color: #555;
          font-family: monospace;
        }

        .prompt-meta {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          font-size: 12px;
        }

        .meta-item {
          display: flex;
          justify-content: space-between;
        }

        .meta-item .label {
          color: #666;
          font-weight: 500;
        }

        .meta-item .value {
          color: #333;
          font-weight: bold;
        }
      `}</style>
    </div>
  );
};

export default PromptsList;
